import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Text from '../components/shared/Text';

// Premium Signup Page - Emerald/Teal Theme, Mobile-First
function Signup() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        password_confirmation: ''
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (formData.password !== formData.password_confirmation) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch('/users.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': (window as any).CSRF_TOKEN || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                },
                body: JSON.stringify({
                    user: {
                        name: formData.name,
                        email: formData.email,
                        password: formData.password,
                        password_confirmation: formData.password_confirmation
                    }
                })
            });

            if (response.ok) {
                window.location.href = '/';
            } else {
                const data = await response.json();
                setError(data.error || 'Registration failed. Please try again.');
            }
        } catch (err) {
            setError('Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-emerald-50/30 flex relative overflow-hidden">
            {/* Abstract Background Shapes */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-teal-200/40 rounded-full blur-[100px] mix-blend-multiply filter opacity-70 animate-blob"></div>
                <div className="absolute top-[30%] right-[-10%] w-[40%] h-[40%] bg-emerald-200/40 rounded-full blur-[100px] mix-blend-multiply filter opacity-70 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[10%] w-[40%] h-[40%] bg-cyan-200/40 rounded-full blur-[100px] mix-blend-multiply filter opacity-70 animate-blob animation-delay-4000"></div>
            </div>

            <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24 z-10 w-full max-w-lg mx-auto lg:max-w-none">
                <div className="mx-auto w-full max-w-sm lg:w-96">
                    <div className="text-center lg:text-left">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-gradient-to-br from-teal-500 to-emerald-600 shadow-2xl shadow-teal-300/50 mb-8 transform hover:rotate-6 transition-all duration-300">
                            <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                        </div>
                        <Text variant="h1" color="primary" className="mb-4">
                            Get Started
                        </Text>
                        <Text variant="body" color="secondary" className="text-lg">
                            Join thousands managing inventory smarter.
                        </Text>
                    </div>

                    <div className="mt-10">
                        <div className="bg-white/90 backdrop-blur-2xl border border-teal-100/50 p-8 rounded-lg shadow-2xl shadow-teal-100/20 ring-1 ring-teal-50">
                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r-2xl animate-fade-in-down">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-rose-500" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <Text variant="small" weight="bold" className="text-rose-700">{error}</Text>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <Text as="label" variant="label" color="slate" className="mb-2 block uppercase tracking-wider" htmlFor="name">
                                        Full Name
                                    </Text>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-teal-500" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            autoComplete="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="block w-full pl-12 pr-4 py-3.5 border-2 border-slate-200 rounded-2xl leading-5 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all duration-200 font-medium shadow-sm"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Text as="label" variant="label" color="slate" className="mb-2 block uppercase tracking-wider" htmlFor="email">
                                        Email address
                                    </Text>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-teal-500" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                            </svg>
                                        </div>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="block w-full pl-12 pr-4 py-3.5 border-2 border-slate-200 rounded-2xl leading-5 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all duration-200 font-medium shadow-sm"
                                            placeholder="you@company.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <Text as="label" variant="label" color="slate" className="mb-2 block uppercase tracking-wider" htmlFor="password">
                                            Password
                                        </Text>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-teal-500" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <input
                                                id="password"
                                                name="password"
                                                type="password"
                                                autoComplete="new-password"
                                                required
                                                minLength={6}
                                                value={formData.password}
                                                onChange={handleChange}
                                                className="block w-full pl-12 pr-4 py-3.5 border-2 border-slate-200 rounded-2xl leading-5 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all duration-200 font-medium shadow-sm"
                                                placeholder="••••••••"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <Text as="label" variant="label" color="slate" className="mb-2 block uppercase tracking-wider" htmlFor="password_confirmation">
                                            Confirm
                                        </Text>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-teal-500" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <input
                                                id="password_confirmation"
                                                name="password_confirmation"
                                                type="password"
                                                autoComplete="new-password"
                                                required
                                                value={formData.password_confirmation}
                                                onChange={handleChange}
                                                className="block w-full pl-12 pr-4 py-3.5 border-2 border-slate-200 rounded-2xl leading-5 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all duration-200 font-medium shadow-sm"
                                                placeholder="••••••••"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-2xl shadow-xl shadow-teal-500/30 text-sm font-black uppercase tracking-widest text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all duration-200 transform hover:scale-[1.02] ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {loading ? (
                                            <div className="flex items-center gap-2">
                                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <Text color="white" weight="bold">Creating account...</Text>
                                            </div>
                                        ) : (
                                            <Text color="white" weight="bold">Create Account</Text>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="mt-8 text-center">
                            <Text variant="small" color="secondary" weight="medium">
                                Already have an account?{' '}
                                <Link to="/login">
                                    <Text as="span" weight="bold" className="text-teal-600 hover:text-teal-700 transition-colors">
                                        Sign in
                                    </Text>
                                </Link>
                            </Text>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side Feature Panel (Hidden on Mobile) */}
            <div className="hidden lg:block relative w-0 flex-1 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-teal-900 to-emerald-900">
                    <img
                        className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-overlay"
                        src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
                        alt="Background"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
                </div>
                <div className="absolute inset-0 flex flex-col justify-end p-20 z-10 text-white">
                    <div className="mb-10 animate-fade-in-up">
                        <span className="inline-block py-2 px-4 rounded-full bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-black uppercase tracking-[0.2em] mb-6">
                            Start Growing Today
                        </span>
                        <Text variant="h1" color="white" className="mb-6 leading-tight text-6xl">
                            Build Your Future <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-300">With Us.</span>
                        </Text>
                        <Text variant="body" color="white" className="opacity-80 text-xl max-w-xl leading-relaxed">
                            Join thousands of businesses that trust us to manage their inventory, orders, and growth. It's free to start.
                        </Text>

                        <div className="mt-10 grid grid-cols-2 gap-8 border-t border-white/10 pt-8">
                            <div>
                                <Text variant="h2" color="white" className="bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">14 Days</Text>
                                <Text variant="small" color="white" weight="bold" className="opacity-50 mt-1 uppercase">Free Trial</Text>
                            </div>
                            <div>
                                <Text variant="h2" color="white" className="bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">24/7</Text>
                                <Text variant="small" color="white" weight="bold" className="opacity-50 mt-1 uppercase">Support Access</Text>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Signup;
